import AirplaneIcon from "@/components/icons/AirplaneIcon";
import BoatIcon from "@/components/icons/BoatIcon";
import NoTransportIcon from "@/components/icons/NoTransportIcon";
import TrainIcon from "@/components/icons/TrainIcon";
import TruckIcon from "@/components/icons/TruckIcon";
import List from "@/components/visualizations/List/List";
import useTranslation from "@/hooks/useTranslation";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { formatAmountWithUnit, formatTransportLabel } from "@/pages/dashboard/utils/formatters";
import { useEffect, useState } from "react";
import Widget from "../../components/Widget/Widget";
import { useTransportImpactTopContributors } from "../../hooks/useTransportImpact";
import { ImpactMetric } from "../../utils/types";

const step = 5;

export function transportIcon(value: string) {
  const mapper = [
    { key: "Truck / Lorry", icon: <TruckIcon /> },
    { key: "Aircraft", icon: <AirplaneIcon /> },
    { key: "Containership", icon: <BoatIcon /> },
    { key: "Commercial vehicle", icon: <TruckIcon /> },
    { key: "Train", icon: <TrainIcon /> },
    { key: "No transport", icon: <NoTransportIcon /> },
  ];

  const match = mapper.find((i) => i.key === value);

  return match ? match.icon : undefined;
}

export default function TransportImpactTopContributors({ metric }: { metric: ImpactMetric }) {
  const translation = useTranslation();
  const [limit, setLimit] = useState(step);
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setLimit(step);
  }, [startDate, endDate]);

  const { data, isLoading, isFetching } = useTransportImpactTopContributors({ metric: metric.key });

  const numMore = Math.min((data?.transport_impact.length || 0) - limit);

  const listData = data?.transport_impact
    ? data?.transport_impact
        .filter((row) => row.travel_mode) // TODO: hack! filter out the empty values
        .slice(0, limit)
        .map((row) => {
          return {
            icon: transportIcon(row.travel_mode),
            label: formatTransportLabel(row.travel_mode),
            value: row[metric.key],
            extraProp: {
              label: "Distance",
              value: formatAmountWithUnit(row.distance, "km", 0),
            },
          };
        })
    : [];

  return (
    <Widget
      title={"Top contributing transport modes"}
      tooltip={translation["dashboard"]["transport"]["top_contributors"]}
    >
      <List
        loading={isLoading || isFetching}
        data={{
          list: listData,
          total: data?.company_transport_impact[metric.key],
        }}
        loadMore={{
          show: numMore > 0,
          numberOfMore: numMore,
          fn: () => setLimit(limit + step),
        }}
        format={metric.formatter}
      />
    </Widget>
  );
}
